import React, { useEffect, useRef, useState } from 'react';
import { formatDateKey, formatDayLabel, slotKey } from './weekUtil';

const CELL_HEIGHT = 22;
const HOVER_ROW_OVERLAY = 'inset 0 0 0 999px rgba(0,0,0,0.07)';
const LONG_PRESS_MS = 350;
const MOVE_CANCEL_THRESHOLD = 10;

export default function AvailabilityGrid({ days, times, selectedSlots, readOnly, onToggle }) {
  const dragRef = useRef(null); // { target, touched: Set } - 마우스는 즉시, 터치는 롱프레스 이후 활성화
  const longPressRef = useRef(null); // { timer, startX, startY, lastX, lastY, dateStr, timeStr, started, scrolling } - 터치 전용
  const wrapRef = useRef(null);
  const [hoveredTime, setHoveredTime] = useState(null);

  const selectedSlotsRef = useRef(selectedSlots);
  useEffect(() => { selectedSlotsRef.current = selectedSlots; }, [selectedSlots]);
  const onToggleRef = useRef(onToggle);
  useEffect(() => { onToggleRef.current = onToggle; }, [onToggle]);

  const beginDrag = (dateStr, timeStr) => {
    const key = slotKey(dateStr, timeStr);
    const target = !selectedSlots.has(key);
    dragRef.current = { target, touched: new Set([key]) };
    onToggle(dateStr, timeStr, target);
  };

  const touchCell = (dateStr, timeStr) => {
    const drag = dragRef.current;
    if (!drag) return;
    const key = slotKey(dateStr, timeStr);
    if (drag.touched.has(key)) return;
    drag.touched.add(key);
    onToggle(dateStr, timeStr, drag.target);
  };

  useEffect(() => {
    const commitTapOrEndDrag = () => {
      const press = longPressRef.current;
      if (press && !press.started && !press.scrolling) {
        clearTimeout(press.timer);
        const key = slotKey(press.dateStr, press.timeStr);
        const target = !selectedSlotsRef.current.has(key);
        onToggleRef.current(press.dateStr, press.timeStr, target);
      }
      longPressRef.current = null;
      dragRef.current = null;
    };
    const cancelWithoutCommit = () => {
      if (longPressRef.current) clearTimeout(longPressRef.current.timer);
      longPressRef.current = null;
      dragRef.current = null;
    };
    window.addEventListener('pointerup', commitTapOrEndDrag);
    window.addEventListener('pointercancel', cancelWithoutCommit);
    return () => {
      window.removeEventListener('pointerup', commitTapOrEndDrag);
      window.removeEventListener('pointercancel', cancelWithoutCommit);
    };
  }, []);

  const handlePointerDown = (e, dateStr, timeStr) => {
    if (readOnly) return;
    if (e.pointerType === 'mouse') {
      beginDrag(dateStr, timeStr);
      return;
    }
    // 터치/펜: 바로 드래그를 시작하지 않고 롱프레스를 기다려서, 일반 스와이프는 스크롤로 남겨둔다
    const pressInfo = {
      timer: null, startX: e.clientX, startY: e.clientY, lastX: e.clientX, lastY: e.clientY,
      dateStr, timeStr, started: false, scrolling: false,
    };
    pressInfo.timer = setTimeout(() => {
      pressInfo.started = true;
      beginDrag(dateStr, timeStr);
    }, LONG_PRESS_MS);
    longPressRef.current = pressInfo;
  };

  const handlePointerEnter = (e, dateStr, timeStr) => {
    if (e.pointerType !== 'mouse') return; // 터치는 포인터가 시작 셀에 캡처되어 enter가 안 옴 -> pointermove에서 처리
    setHoveredTime(timeStr);
    if (!readOnly && dragRef.current) touchCell(dateStr, timeStr);
  };

  const cellFromPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const cell = el && el.closest ? el.closest('td[data-date]') : null;
    return cell ? { dateStr: cell.dataset.date, timeStr: cell.dataset.time } : null;
  };

  const handleTablePointerMove = (e) => {
    if (e.pointerType === 'mouse' || readOnly) return;

    const press = longPressRef.current;
    if (press && !press.started) {
      if (!press.scrolling) {
        const dx = e.clientX - press.startX;
        const dy = e.clientY - press.startY;
        if (Math.hypot(dx, dy) > MOVE_CANCEL_THRESHOLD) {
          clearTimeout(press.timer);
          press.scrolling = true; // 스크롤 의도로 보고 드래그 후보를 취소, 이 제스처는 우리가 직접 스크롤시킨다
        }
      }
      if (press.scrolling) {
        // touch-action: none이라 브라우저 기본 스크롤이 아예 안 걸리므로, 손가락 이동량만큼 직접 스크롤한다
        e.preventDefault();
        const dxMove = press.lastX - e.clientX;
        const dyMove = press.lastY - e.clientY;
        if (wrapRef.current) wrapRef.current.scrollLeft += dxMove;
        window.scrollBy(0, dyMove);
        press.lastX = e.clientX;
        press.lastY = e.clientY;
      }
      return;
    }

    if (dragRef.current) {
      e.preventDefault(); // 드래그가 확정된 뒤부터만 셀 선택으로 처리한다
      const cell = cellFromPoint(e.clientX, e.clientY);
      if (cell) touchCell(cell.dateStr, cell.timeStr);
    }
  };

  return (
    <div ref={wrapRef} style={{ overflowX: 'auto', userSelect: 'none' }}>
      <table
        style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560 }}
        onMouseLeave={() => setHoveredTime(null)}
        onPointerMove={handleTablePointerMove}
      >
        <thead>
          <tr>
            <th style={{ width: 56, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }} />
            {days.map((d) => (
              <th
                key={formatDateKey(d)}
                style={{ padding: '4px 2px', fontSize: 12, fontWeight: 500, borderBottom: '1px solid #f0f0f0' }}
              >
                {formatDayLabel(d)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {times.map((time) => {
            const rowHovered = time === hoveredTime;
            return (
              <tr key={time}>
                <td
                  style={{
                    fontSize: 11,
                    color: rowHovered ? '#1677ff' : '#888',
                    fontWeight: rowHovered ? 700 : 400,
                    textAlign: 'right',
                    paddingRight: 6,
                    position: 'sticky',
                    left: 0,
                    background: '#fff',
                    height: CELL_HEIGHT,
                  }}
                >
                  {time}
                </td>
                {days.map((d) => {
                  const dateStr = formatDateKey(d);
                  const key = slotKey(dateStr, time);
                  const available = selectedSlots.has(key);
                  return (
                    <td
                      key={key}
                      data-date={dateStr}
                      data-time={time}
                      onPointerDown={(e) => handlePointerDown(e, dateStr, time)}
                      onPointerEnter={(e) => handlePointerEnter(e, dateStr, time)}
                      style={{
                        height: CELL_HEIGHT,
                        border: '1px solid #f0f0f0',
                        background: available ? '#95de64' : '#fafafa',
                        boxShadow: rowHovered ? HOVER_ROW_OVERLAY : undefined,
                        cursor: readOnly ? 'default' : 'pointer',
                        touchAction: 'none',
                        transition: 'background 0.1s',
                      }}
                    />
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
